import { Navigate, useParams } from "react-router-dom";

import RoleGuardRoute from "../../../components/layout/RoleGuardRoute";

import CommissionGroupsPage from "./CommissionGroups/CommissionGroupsPage";
import CommissionRulesPage from "./CommissionRules/CommissionRulesPage";
import MarketGrowthAuditLogsPage from "./AuditLogs/MarketGrowthAuditLogsPage";
import GrowthAnalyticsPage from "../management/growth-analytics/index.js";
import MarketGrowthDashboardPage from "./Dashboard/MarketGrowthDashboardPage";
import MarketGrowthFraudAlertsPage from "./FraudAlerts/MarketGrowthFraudAlertsPage";
import LeadCampaignDashboardPage from "./LeadCampaigns/LeadCampaignDashboardPage";
import LeadCampaignDetailPage from "./LeadCampaigns/LeadCampaignDetailPage";
import LeadCampaignReportsPage from "./LeadCampaigns/LeadCampaignReportsPage";
import LeadCampaignsPage from "./LeadCampaigns/LeadCampaignsPage";
import LeadContactGroupDetailPage from "./LeadCampaigns/LeadContactGroupDetailPage";
import LeadContactGroupsPage from "./LeadCampaigns/LeadContactGroupsPage";
import MarketGrowthNetworkPage from "./Network/MarketGrowthNetworkPage";
import MarketGrowthPartnerApprovalsPage from "./PartnerApprovals/MarketGrowthPartnerApprovalsPage";
import MarketGrowthPartnerLeadsPage from "./PartnerLeads/MarketGrowthPartnerLeadsPage";
import MarketGrowthPartnersPage from "./Partners/MarketGrowthPartnersPage";
import MarketGrowthSettingsPage from "./Settings/MarketGrowthSettingsPage";
import PartnerCommissionsPage from "./Commissions/PartnerCommissionsPage";
import PartnerPayoutsPage from "./Payouts/PartnerPayoutsPage";
import ReferralAttributionsPage from "./Attributions/ReferralAttributionsPage";

import PartnerCommissionDashboardPage from "../../partner-commission/PartnerCommissionDashboardPage";
import PartnerCommissionsPortalPage from "../../partner-commission/PartnerCommissionsPage";
import PartnerPayoutsPortalPage from "../../partner-commission/PartnerPayoutsPage";
import PartnerLeadsPage from "../../partner-commission/PartnerLeadsPage";
import PartnerRegisteredWithoutProjectPage from "../../partner-commission/PartnerRegisteredWithoutProjectPage";
import PartnerProfilePage from "../../partner-commission/PartnerProfilePage";
import PartnerReferralPage from "../../partner-commission/PartnerReferralPage";
import PartnerTeamPage from "../../partner-commission/PartnerTeamPage";
import AssistedRegistrationExpertReviewPage from "./AssistedRegistration/AssistedRegistrationExpertReviewPage";
import AssistedRegistrationPage from "../../partner-commission/AssistedRegistrationPage";

import {
  LEAD_CAMPAIGN_FLAT_REDIRECTS,
  buildMarketGrowthPath,
} from "./marketGrowthMenuRouteConfig.js";

const withAdminGuard = (element) => (
  <RoleGuardRoute requireAdmin>{element}</RoleGuardRoute>
);

const withPartnerGuard = (element) => (
  <RoleGuardRoute requirePartner>{element}</RoleGuardRoute>
);

const LeadCampaignFlatDetailRedirect = ({ nestedPath }) => {
  const { id } = useParams();
  return (
    <Navigate
      to={buildMarketGrowthPath(...nestedPath.split("/"), id)}
      replace
    />
  );
};

const legacyManagementMarketGrowthRedirects = [
  { from: "Management/MarketGrowthDashboard", to: buildMarketGrowthPath("Dashboard") },
  { from: "Management/MarketGrowthPartners", to: buildMarketGrowthPath("Partners") },
  { from: "Management/MarketGrowthNetwork", to: buildMarketGrowthPath("Network") },
  { from: "Management/MarketGrowthPartnerApprovals", to: buildMarketGrowthPath("PartnerApprovals") },
  { from: "Management/MarketGrowthCommissionGroups", to: buildMarketGrowthPath("CommissionGroups") },
  { from: "Management/MarketGrowthCommissionRules", to: buildMarketGrowthPath("CommissionRules") },
  { from: "Management/MarketGrowthAttributions", to: buildMarketGrowthPath("Attributions") },
  { from: "Management/MarketGrowthCommissions", to: buildMarketGrowthPath("Commissions") },
  { from: "Management/MarketGrowthPayouts", to: buildMarketGrowthPath("Payouts") },
  { from: "Management/MarketGrowthFraudAlerts", to: buildMarketGrowthPath("FraudAlerts") },
  { from: "Management/MarketGrowthAuditLogs", to: buildMarketGrowthPath("AuditLogs") },
  { from: "Management/MarketGrowthSettings", to: buildMarketGrowthPath("Settings") },
  { from: "Management/GrowthAnalytics", to: buildMarketGrowthPath("GrowthAnalytics") },
].map(({ from, to }) => ({
  path: from,
  element: <Navigate to={to} replace />,
}));

/** Redirect flat Lead Campaign paths to canonical nested menu routes. */
const leadCampaignFlatRedirectEntries = Object.entries(
  LEAD_CAMPAIGN_FLAT_REDIRECTS
).flatMap(([controller, nestedPath]) => [
  {
    path: `MarketGrowth/${controller}`,
    element: <Navigate to={buildMarketGrowthPath(...nestedPath.split("/"))} replace />,
  },
  {
    path: `MarketGrowth/${controller}/:id`,
    element: <LeadCampaignFlatDetailRedirect nestedPath={nestedPath} />,
  },
]);

/** Standard SLN menu controllers (MNG_ModuleLists) -> route entries */
export const marketGrowthSlnRouteEntries = [
  {
    path: "MarketGrowth",
    element: <Navigate to={buildMarketGrowthPath("Dashboard")} replace />,
  },
  {
    path: "MarketGrowth/Dashboard",
    element: withAdminGuard(<MarketGrowthDashboardPage />),
  },
  {
    path: "MarketGrowth/Partners",
    element: withAdminGuard(<MarketGrowthPartnersPage />),
  },
  {
    path: "MarketGrowth/Network",
    element: withAdminGuard(<MarketGrowthNetworkPage />),
  },
  {
    path: "MarketGrowth/PartnerApprovals",
    element: withAdminGuard(<MarketGrowthPartnerApprovalsPage />),
  },
  {
    path: "MarketGrowth/CommissionGroups",
    element: withAdminGuard(<CommissionGroupsPage />),
  },
  {
    path: "MarketGrowth/CommissionRules",
    element: withAdminGuard(<CommissionRulesPage />),
  },
  {
    path: "MarketGrowth/Attributions",
    element: withAdminGuard(<ReferralAttributionsPage />),
  },
  {
    path: "MarketGrowth/Commissions",
    element: withAdminGuard(<PartnerCommissionsPage />),
  },
  {
    path: "MarketGrowth/Payouts",
    element: withAdminGuard(<PartnerPayoutsPage />),
  },
  {
    path: "MarketGrowth/FraudAlerts",
    element: withAdminGuard(<MarketGrowthFraudAlertsPage />),
  },
  {
    path: "MarketGrowth/AuditLogs",
    element: withAdminGuard(<MarketGrowthAuditLogsPage />),
  },
  {
    path: "MarketGrowth/Settings",
    element: withAdminGuard(<MarketGrowthSettingsPage />),
  },
  {
    path: "MarketGrowth/LeadCampaigns",
    element: (
      <Navigate to={buildMarketGrowthPath("LeadCampaigns", "Dashboard")} replace />
    ),
  },
  {
    path: "MarketGrowth/LeadCampaigns/Dashboard",
    element: withAdminGuard(<LeadCampaignDashboardPage />),
  },
  {
    path: "MarketGrowth/LeadCampaigns/Groups",
    element: withAdminGuard(<LeadContactGroupsPage />),
  },
  {
    path: "MarketGrowth/LeadCampaigns/Groups/:id",
    element: withAdminGuard(<LeadContactGroupDetailPage />),
  },
  {
    path: "MarketGrowth/LeadCampaigns/Campaigns",
    element: withAdminGuard(<LeadCampaignsPage />),
  },
  {
    path: "MarketGrowth/LeadCampaigns/Campaigns/:id",
    element: withAdminGuard(<LeadCampaignDetailPage />),
  },
  {
    path: "MarketGrowth/LeadCampaigns/Reports",
    element: withAdminGuard(<LeadCampaignReportsPage />),
  },
  ...leadCampaignFlatRedirectEntries,
  {
    path: "MarketGrowth/PartnerLeads",
    element: withAdminGuard(<MarketGrowthPartnerLeadsPage />),
  },
  {
    path: "MarketGrowth/AssistedRegistrationExpertReview",
    element: withAdminGuard(<AssistedRegistrationExpertReviewPage />),
  },
  {
    path: "MarketGrowth/GrowthAnalytics",
    element: withAdminGuard(<GrowthAnalyticsPage />),
  },
  ...legacyManagementMarketGrowthRedirects,
  {
    path: "Partner",
    element: withPartnerGuard(<PartnerCommissionDashboardPage />),
  },
  {
    path: "Partner/PartnerDashboard",
    element: withPartnerGuard(<PartnerCommissionDashboardPage />),
  },
  {
    path: "Partner/PartnerLeads",
    element: withPartnerGuard(<PartnerLeadsPage />),
  },
  {
    path: "Partner/PartnerRegisteredWithoutProject",
    element: withPartnerGuard(<PartnerRegisteredWithoutProjectPage />),
  },
  {
    path: "Partner/PartnerReferral",
    element: withPartnerGuard(<PartnerReferralPage />),
  },
  {
    path: "Partner/PartnerTeam",
    element: withPartnerGuard(<PartnerTeamPage />),
  },
  {
    path: "Partner/PartnerCommissions",
    element: withPartnerGuard(<PartnerCommissionsPortalPage />),
  },
  {
    path: "Partner/PartnerPayouts",
    element: withPartnerGuard(<PartnerPayoutsPortalPage />),
  },
  {
    path: "Partner/PartnerProfile",
    element: withPartnerGuard(<PartnerProfilePage />),
  },
  {
    path: "Partner/AssistedRegistration",
    element: withPartnerGuard(<AssistedRegistrationPage />),
  },
];

/** Legacy non-SLN paths kept as redirects for old bookmarks. */
export const marketGrowthLegacyRedirectEntries = [
  {
    path: "admin/market-growth",
    children: [
      { index: true, element: <Navigate to={buildMarketGrowthPath("Dashboard")} replace /> },
      { path: "dashboard", element: <Navigate to={buildMarketGrowthPath("Dashboard")} replace /> },
      { path: "partners", element: <Navigate to={buildMarketGrowthPath("Partners")} replace /> },
      { path: "network", element: <Navigate to={buildMarketGrowthPath("Network")} replace /> },
      { path: "partner-approvals", element: <Navigate to={buildMarketGrowthPath("PartnerApprovals")} replace /> },
      { path: "commission-groups", element: <Navigate to={buildMarketGrowthPath("CommissionGroups")} replace /> },
      { path: "commission-rules", element: <Navigate to={buildMarketGrowthPath("CommissionRules")} replace /> },
      { path: "attributions", element: <Navigate to={buildMarketGrowthPath("Attributions")} replace /> },
      { path: "commissions", element: <Navigate to={buildMarketGrowthPath("Commissions")} replace /> },
      { path: "payouts", element: <Navigate to={buildMarketGrowthPath("Payouts")} replace /> },
      { path: "fraud-alerts", element: <Navigate to={buildMarketGrowthPath("FraudAlerts")} replace /> },
      { path: "audit-logs", element: <Navigate to={buildMarketGrowthPath("AuditLogs")} replace /> },
      { path: "settings", element: <Navigate to={buildMarketGrowthPath("Settings")} replace /> },
      {
        path: "lead-campaigns",
        element: <Navigate to={buildMarketGrowthPath("LeadCampaigns", "Dashboard")} replace />,
      },
      { path: "partner-leads", element: <Navigate to={buildMarketGrowthPath("PartnerLeads")} replace /> },
    ],
  },
  {
    path: "partner/commission",
    children: [
      { index: true, element: <Navigate to="/SLN/Partner/PartnerDashboard" replace /> },
      { path: "dashboard", element: <Navigate to="/SLN/Partner/PartnerDashboard" replace /> },
      { path: "leads", element: <Navigate to="/SLN/Partner/PartnerLeads" replace /> },
      { path: "referral", element: <Navigate to="/SLN/Partner/PartnerReferral" replace /> },
      { path: "team", element: <Navigate to="/SLN/Partner/PartnerTeam" replace /> },
      { path: "commissions", element: <Navigate to="/SLN/Partner/PartnerCommissions" replace /> },
      { path: "payouts", element: <Navigate to="/SLN/Partner/PartnerPayouts" replace /> },
      { path: "profile", element: <Navigate to="/SLN/Partner/PartnerProfile" replace /> },
      { path: "assisted-registration", element: <Navigate to="/SLN/Partner/AssistedRegistration" replace /> },
    ],
  },
];
